import { format } from 'date-fns';
import { KeyRound, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUpdateUserSecurityState, useUserSecurityLog } from '../hooks';
import type { User } from '../types';

interface SecurityLogTabProps {
  user: User;
  isFullAdmin: boolean;
}

const EVENT_LABELS: Record<string, string> = {
  account_locked: 'Account locked',
  account_unlock_requested: 'Unlock requested',
  account_unlocked: 'Account unlocked',
  password_reset_requested: 'Password reset requested',
  password_reset_completed: 'Password reset completed',
  password_changed: 'Password changed',
  password_set_by_admin: 'Password set by admin',
  user_enabled: 'User enabled',
  user_disabled: 'User disabled',
  force_password_reset_enabled: 'Password reset enabled',
  force_password_reset_disabled: 'Password reset disabled',
};

function eventVariant(eventType: string): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
  if (eventType === 'account_locked' || eventType === 'user_disabled') return 'danger';
  if (eventType.includes('reset') || eventType.includes('unlock_requested')) return 'warning';
  if (eventType === 'account_unlocked' || eventType === 'user_enabled') return 'success';
  if (eventType.includes('password')) return 'info';
  return 'secondary';
}

function actorLabel(actorName?: string | null, actorEmail?: string | null, actorType?: string | null) {
  if (actorName) return actorName;
  if (actorEmail) return actorEmail;
  if (actorType === 'system') return 'System';
  return actorType || 'Unknown';
}

export function SecurityLogTab({ user, isFullAdmin }: SecurityLogTabProps) {
  const securityLogQuery = useUserSecurityLog(user._id, { refetchInterval: 5000 });
  const updateSecurityState = useUpdateUserSecurityState();

  const pending = updateSecurityState.isPending;

  const handleEnabledChange = (checked: boolean) => {
    updateSecurityState.mutate({
      userId: user._id,
      data: {
        enabled: checked,
        reason: checked ? 'admin_unlocked_account' : 'admin_locked_account',
      },
    });
  };

  const handleForceResetChange = (checked: boolean) => {
    updateSecurityState.mutate({
      userId: user._id,
      data: {
        force_password_reset: checked,
        reason: checked ? 'admin_required_password_reset' : 'admin_cleared_password_reset',
      },
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Account</span>
            </div>
            <Switch
              checked={user.enabled}
              onCheckedChange={handleEnabledChange}
              disabled={!isFullAdmin || pending}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {user.enabled ? 'Enabled' : 'Disabled'}
          </p>
        </div>

        <div className="rounded-md border p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Password reset</span>
            </div>
            <Switch
              checked={Boolean(user.force_password_reset)}
              onCheckedChange={handleForceResetChange}
              disabled={!isFullAdmin || pending}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {user.force_password_reset ? 'Required on next login' : 'Not required'}
          </p>
        </div>

      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">Security events</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => securityLogQuery.refetch()}
          disabled={securityLogQuery.isFetching}
          aria-label="Refresh security events"
          title="Refresh security events"
        >
          <RefreshCw className={`h-4 w-4 ${securityLogQuery.isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {securityLogQuery.isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {securityLogQuery.error && (
        <div className="py-8 text-center text-muted-foreground">
          Failed to load security log
        </div>
      )}

      {!securityLogQuery.isLoading && !securityLogQuery.error && (
        securityLogQuery.data?.events.length ? (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {securityLogQuery.data.events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(event.created_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant={eventVariant(event.event_type)}>
                          {EVENT_LABELS[event.event_type] || event.event_type}
                        </Badge>
                        {event.reason && (
                          <div className="text-xs text-muted-foreground">{event.reason}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {actorLabel(event.actor_display_name, event.actor_email, event.actor_type)}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {event.ip || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No security events
          </div>
        )
      )}
    </div>
  );
}
